import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import "./AliExpressReviews.css";

const AliExpressReviews = ({ productId, onClose }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("all"); // all, 5, 4, 3, 2, 1, withPhotos
  const [sortBy, setSortBy] = useState("recent"); // recent, helpful

  // Form state
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [images, setImages] = useState([]);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadReviews = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/aliexpress/reviews/${productId}`
      );
      setReviews(res.data.reviews || []);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar avaliações:", error);
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      alert("Máximo de 5 imagens");
      return;
    }

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmitReview = async () => {
    if (!comment.trim()) {
      alert("Por favor, escreva um comentário");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/aliexpress/reviews/create`,
        {
          productId,
          rating,
          comment,
          images,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setReviews([res.data.review, ...reviews]);
      setShowForm(false);
      setRating(5);
      setComment("");
      setImages([]);
      alert("Avaliação enviada com sucesso!");
    } catch (error) {
      alert(error.response?.data?.message || "Erro ao enviar avaliação");
    } finally {
      setSubmitting(false);
    }
  };

  const calculateStats = () => {
    if (reviews.length === 0) return { average: 0, distribution: {} };

    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    const average = (sum / reviews.length).toFixed(1);

    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((r) => {
      distribution[r.rating] = (distribution[r.rating] || 0) + 1;
    });

    return { average, distribution };
  };

  const renderStars = (count, interactive = false, size = 20) => {
    const stars = [];
    const displayCount = interactive ? (hoveredStar || count) : count;

    for (let i = 1; i <= 5; i++) {
      stars.push(
        <svg
          key={i}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={i <= displayCount ? "#ffc107" : "#e0e0e0"}
          style={{ cursor: interactive ? "pointer" : "default" }}
          onMouseEnter={() => interactive && setHoveredStar(i)}
          onMouseLeave={() => interactive && setHoveredStar(0)}
          onClick={() => interactive && setRating(i)}
        >
          <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
        </svg>
      );
    }
    return stars;
  };

  const filteredReviews = reviews
    .filter((review) => {
      if (filter === "all") return true;
      if (filter === "withPhotos") return review.images && review.images.length > 0;
      return review.rating === parseInt(filter);
    })
    .sort((a, b) => {
      if (sortBy === "helpful") return (b.helpful || 0) - (a.helpful || 0);
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="reviews-modal-overlay" onClick={onClose}>
        <div className="reviews-modal" onClick={(e) => e.stopPropagation()}>
          <div className="reviews-loading">
            <div className="spinner"></div>
            <p>Carregando avaliações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-modal-overlay" onClick={onClose}>
      <div className="reviews-modal" onClick={(e) => e.stopPropagation()}>
        <button className="btn-close-reviews" onClick={onClose}>
          ✕
        </button>

        <div className="reviews-header">
          <h2>Avaliações do Produto</h2>
          <button className="btn-write-review" onClick={() => setShowForm(!showForm)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
            Escrever Avaliação
          </button>
        </div>

        {/* Write Review Form */}
        {showForm && (
          <div className="review-form">
            <h3>Sua Avaliação</h3>

            <div className="form-rating">
              <label>Classificação:</label>
              <div className="rating-stars">{renderStars(rating, true, 32)}</div>
              <span className="rating-text">
                {rating === 5 ? "Excelente" : rating === 4 ? "Muito Bom" : rating === 3 ? "Bom" : rating === 2 ? "Regular" : "Ruim"}
              </span>
            </div>

            <div className="form-comment">
              <label>Comentário:</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Compartilhe sua experiência com o produto..."
                rows="5"
              />
            </div>

            <div className="form-images">
              <label>Adicionar Fotos (opcional):</label>
              <div className="image-upload-area">
                {images.map((img, idx) => (
                  <div key={idx} className="uploaded-image">
                    <img src={img} alt="" />
                    <button
                      className="btn-remove-image"
                      onClick={() => setImages(images.filter((_, i) => i !== idx))}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <label className="upload-button">
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} />
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4 2 3 3-4 4 5H5z" />
                    </svg>
                    <span>Adicionar Foto</span>
                  </label>
                )}
              </div>
              <p className="image-hint">Máximo de 5 fotos</p>
            </div>

            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
              <button className="btn-submit-review" onClick={handleSubmitReview} disabled={submitting}>
                {submitting ? "Enviando..." : "Publicar Avaliação"}
              </button>
            </div>
          </div>
        )}

        {/* Stats Summary */}
        <div className="reviews-stats">
          <div className="stats-summary">
            <div className="average-rating">
              <span className="rating-number">{stats.average}</span>
              <div className="rating-stars-small">{renderStars(Math.round(stats.average), false, 18)}</div>
              <span className="total-reviews">{reviews.length} avaliações</span>
            </div>

            <div className="rating-distribution">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = stats.distribution[star] || 0;
                const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;

                return (
                  <div key={star} className="distribution-row">
                    <span className="star-label">{star} ★</span>
                    <div className="distribution-bar">
                      <div className="bar-fill" style={{ width: `${percentage}%` }}></div>
                    </div>
                    <span className="distribution-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="reviews-filters">
          <div className="filter-buttons">
            <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>
              Todas ({reviews.length})
            </button>
            <button className={filter === "withPhotos" ? "active" : ""} onClick={() => setFilter("withPhotos")}>
              Com Fotos ({reviews.filter((r) => r.images?.length > 0).length})
            </button>
            {[5, 4, 3, 2, 1].map((star) => (
              <button key={star} className={filter === star.toString() ? "active" : ""} onClick={() => setFilter(star.toString())}>
                {star} ★ ({stats.distribution[star] || 0})
              </button>
            ))}
          </div>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="recent">Mais Recentes</option>
            <option value="helpful">Mais Úteis</option>
          </select>
        </div>

        {/* Reviews List */}
        <div className="reviews-list">
          {filteredReviews.length === 0 ? (
            <div className="no-reviews">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
              </svg>
              <p>Nenhuma avaliação encontrada</p>
            </div>
          ) : (
            filteredReviews.map((review) => (
              <div key={review._id} className="review-item">
                <div className="review-header">
                  <div className="reviewer-info">
                    <img src={review.user.picture} alt={review.user.name} />
                    <div>
                      <span className="reviewer-name">{review.user.name}</span>
                      {review.verified && (
                        <span className="verified-badge">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                          </svg>
                          Compra Verificada
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="review-date">{new Date(review.createdAt).toLocaleDateString("pt-BR")}</div>
                </div>

                <div className="review-rating">{renderStars(review.rating, false, 16)}</div>

                <p className="review-comment">{review.comment}</p>

                {review.images && review.images.length > 0 && (
                  <div className="review-images">
                    {review.images.map((img, idx) => (
                      <img key={idx} src={img} alt="" />
                    ))}
                  </div>
                )}

                <div className="review-actions">
                  <button className="btn-helpful">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
                    </svg>
                    Útil {review.helpful > 0 && `(${review.helpful})`}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AliExpressReviews;
